import {
  Avatar,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  SxProps,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { Fragment, useCallback, useMemo, useState } from 'react'
import { IconButton, SkeletonText } from 'utoniq-ui'

import {
  Maybe,
  MemberBoxFragment,
  MembershipInterval,
  PaymentMethod,
} from '@/clients/utoniq-core/schema'
import { Head } from '@/components/layouts'
import { ConsoleBaseLayout } from '@/components/layouts/ConsoleBaseLayout'
import { MembershipProductBox, MemberWhetTag } from '@/components/organisms'
import { TableScrollFetcher } from '@/components/organisms/functionals'
import { ArtistThemeProvider, styleSets } from '@/components/styles'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { MembershipSubscriptionProduct } from '@/core/membership'

export * from './fragments'

export type State = {
  artistId: string
  enableFavoriteMembers: boolean
  loading: boolean
  refetching: boolean
  hasNextPage: boolean
  planId: string
  product?: Maybe<MembershipSubscriptionProduct>
  members: Maybe<{ edges: { node: MemberBoxFragment }[] }>
  patronCount: number
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onFetchMore: () => Promise<void>
  onExportCsv: (productId: string) => Promise<void>
}

export const ConsoleMembershipDetailTemplate: Template<State, Action> = ({
  state,
  onFetchMore,
  onExportCsv,
}) => {
  const { t } = useTranslation('consoleMembershipDetail')
  const { routerNames, routerPages, generatePath } = useRouter()

  return (
    <ArtistThemeProvider artistId={state.artistId}>
      <Head title={routerNames.ConsoleMembershipDetail} />

      <ConsoleBaseLayout
        margin
        header={{
          back: true,
          defaultBackPage: routerPages.ConsoleMembership,
          title: state.product?.title,
          right: (
            <Stack direction="row" spacing={1} sx={{ mr: -2 }}>
              {/*
                @todo 公開・非公開・削除対応時に復活 / https://app.clickup.com/t/908127/UT-10160
                <IconButton.Trash
                  size="small"
                  color="white"
                  onClick={() => setOpen(true)}
                />
                */}
              <IconButton.Pencil
                size="small"
                color="white"
                href={generatePath(routerPages.ConsoleMembershipEdit, {
                  planId: state.planId,
                })}
              />
            </Stack>
          ),
        }}
      >
        <Stack sx={{ pt: 2, pb: 5 }}>
          <Typography variant="h5" sx={{ pt: 4, pb: 3 }}>
            {t('membershipDetails')}
          </Typography>
          {state.product ? (
            <MembershipProductBox
              isDetail
              product={state.product}
              // @todo 公開・非公開・削除対応時に復活 / https://app.clickup.com/t/908127/UT-10160
              // onPrivate={onPrivate}
              // onOpen={onOpen}
              // opening={state.opening}
              // privating={state.privating}
            />
          ) : (
            <Skeleton variant="rectangular" width="100%" height={360} />
          )}
        </Stack>
        <MemberContents
          members={state.members}
          enableFavoriteMembers={state.enableFavoriteMembers}
          loading={state.loading || state.refetching}
          hasNextPage={state.hasNextPage}
          patronCount={state.patronCount}
          product={state.product}
          onFetchMore={onFetchMore}
          onExportCsv={onExportCsv}
        />
        {/*
          @todo 公開・非公開・削除対応時に復活 / https://app.clickup.com/t/908127/UT-10160
          <InformationModal
            open={open}
            onClose={() => setOpen(false)}
            title={t('deleteMembershipTitle', {
              title: state.plan?.title ?? '',
            })}
            content={t('deleteMembershipDesc')}
            cancelText={t('cancel')}
            buttons={
              <Button
                size="large"
                sx={{ minWidth: 212 }}
                onClick={handleDelete}
                loading={state.deleting}
              >
                {t('okConfirm')}
              </Button>
            }
          />
          */}
      </ConsoleBaseLayout>
    </ArtistThemeProvider>
  )
}

type MemberContentsProps = {
  members: State['members']
  enableFavoriteMembers: boolean
  loading: boolean
  hasNextPage: boolean
  patronCount: number
  product?: Maybe<MembershipSubscriptionProduct>
  onFetchMore: () => Promise<void>
  onExportCsv: (productId: string) => Promise<void>
}
const MemberContents: Cell<MemberContentsProps> = ({
  members,
  enableFavoriteMembers,
  loading,
  patronCount,
  hasNextPage,
  product,
  onFetchMore,
  onExportCsv,
}) => {
  const { t } = useTranslation('consoleMembershipDetail')
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const getPlanInterval = useCallback(
    (planId: string) => {
      return product?.plans?.find(plan => plan.planId === planId)?.interval
    },
    [product]
  )

  // 会員番号の表示判定
  // 会員番号は全員あるかないかの二択のため、最初のメンバーの会員番号の有無で判定
  const shouldShowMemberNumber = useMemo(() => {
    return members?.edges?.[0]?.node?.formattedMembershipNumber
  }, [members?.edges])

  return (
    <CellFrame sx={{ pt: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="h5">{t('registeredMemberNumber')}</Typography>
          <Typography variant="subtitle1" color="text.hint">
            {t('people', { people: patronCount })}
          </Typography>
        </Stack>
        {/*
        @todo filter実装時に復活 / https://app.clickup.com/t/908127/UT-10160
        <IconButton.Slider edge="end" size="medium" />
        */}
        {!!product?.productId && (
          <>
            <IconButton.More
              edge="end"
              onClick={event => setAnchorEl(event.currentTarget)}
            />
            <Menu
              open={!!anchorEl}
              anchorEl={anchorEl}
              onClose={() => setAnchorEl(null)}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              <MenuItem
                sx={{ minWidth: 200 }}
                disabled={!members?.edges?.length}
                onClick={() => {
                  onExportCsv(product?.productId ?? '')
                  setAnchorEl(null)
                }}
              >
                {t('exportCsv', 'CSVエクスポート')}
              </MenuItem>
            </Menu>
          </>
        )}
      </Stack>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ border: 'none', py: 2, px: 0, minWidth: 116 }}>
                <Typography variant="overline" color="text.hint" component="p">
                  {t('username', 'ユーザー名')}
                </Typography>
              </TableCell>
              <TableCell sx={{ border: 'none', py: 2, px: 1, minWidth: 80 }}>
                <Typography variant="overline" color="text.hint" component="p">
                  {t('fullName', '氏名')}
                </Typography>
              </TableCell>
              <TableCell sx={{ border: 'none', py: 2, px: 1, minWidth: 80 }}>
                <Typography variant="overline" color="text.hint" component="p">
                  {t('email', 'メールアドレス')}
                </Typography>
              </TableCell>
              <TableCell sx={{ border: 'none', py: 2, px: 1, minWidth: 80 }}>
                <Typography variant="overline" color="text.hint" component="p">
                  {t('gender', '性別')}
                </Typography>
              </TableCell>
              <TableCell sx={{ border: 'none', py: 2, px: 1, minWidth: 80 }}>
                <Typography variant="overline" color="text.hint" component="p">
                  {t('birthday', '生年月日')}
                </Typography>
              </TableCell>
              <TableCell sx={{ border: 'none', py: 2, px: 1, minWidth: 80 }}>
                <Typography variant="overline" color="text.hint" component="p">
                  {t('phoneNumber', '電話番号')}
                </Typography>
              </TableCell>
              <TableCell sx={{ border: 'none', py: 2, px: 1, minWidth: 80 }}>
                <Typography variant="overline" color="text.hint" component="p">
                  {t('address', '住所')}
                </Typography>
              </TableCell>
              {/* 会員番号がある場合のみ表示 */}
              {shouldShowMemberNumber && (
                <TableCell sx={{ border: 'none', minWidth: 84, py: 2, px: 1 }}>
                  <Typography
                    variant="overline"
                    color="text.hint"
                    component="p"
                  >
                    {t('memberNumber', '会員番号')}
                  </Typography>
                </TableCell>
              )}
              <TableCell sx={{ border: 'none', minWidth: 120, py: 2, px: 1 }}>
                <Typography variant="overline" color="text.hint" component="p">
                  {t('durationMembershipDate', '継続期間/入会日')}
                </Typography>
              </TableCell>
              {enableFavoriteMembers && (
                <TableCell sx={{ border: 'none', minWidth: 84, py: 2, px: 1 }}>
                  <Typography
                    variant="overline"
                    color="text.hint"
                    component="p"
                  >
                    {t('favoriteMember', '推しメンバー')}
                  </Typography>
                </TableCell>
              )}
              <TableCell sx={{ border: 'none', minWidth: 84, py: 2, px: 1 }}>
                <Typography variant="overline" color="text.hint" component="p">
                  {t('paymentMethod', '支払い方法')}
                </Typography>
              </TableCell>
              <TableCell sx={{ border: 'none', minWidth: 80, py: 2, px: 1 }}>
                <Typography variant="overline" color="text.hint" component="p">
                  {t('orderPerPurchase', '注文/購入回数')}
                </Typography>
              </TableCell>
              <TableCell sx={{ border: 'none', minWidth: 80, py: 2, px: 1 }}>
                <Typography variant="overline" color="text.hint" component="p">
                  {t('ownedPerUnused', '所有アイテム/\n未使用チケット')}
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableScrollFetcher
            nodes={members?.edges.map(({ node }) => (
              <Fragment key={node.user.id}>
                <TableCellCustom
                  userId={node.user.id}
                  sx={{ px: 0, minWidth: 116 }}
                >
                  <Stack
                    direction="row"
                    sx={{ maxWidth: '100%', width: 116 }}
                    spacing={2}
                    alignItems="center"
                  >
                    <Avatar
                      src={node.user.avatar?.smallUri ?? ''}
                      sx={{ width: 28, height: 28 }}
                    />
                    <Typography variant="subtitle1" sx={styleSets.lineCamp(1)}>
                      {node.user.displayName}
                    </Typography>
                  </Stack>
                </TableCellCustom>
                <TableCellCustom userId={node.user.id} sx={{ minWidth: 100 }}>
                  <Stack sx={{ minWidth: 0, width: 100 }}>
                    <Typography variant="overline" sx={styleSets.lineCamp(1)}>
                      {node.user.fullName}
                    </Typography>
                  </Stack>
                </TableCellCustom>
                <TableCellCustom userId={node.user.id} sx={{ minWidth: 100 }}>
                  <Stack sx={{ minWidth: 0, width: 100 }}>
                    <Typography variant="overline" sx={styleSets.lineCamp(1)}>
                      {node.user.email}
                    </Typography>
                  </Stack>
                </TableCellCustom>
                <TableCellCustom userId={node.user.id} sx={{ minWidth: 100 }}>
                  <Typography variant="overline">
                    {!node.user.gender ? '' : t(`gender_${node.user.gender}`)}
                  </Typography>
                </TableCellCustom>
                <TableCellCustom userId={node.user.id} sx={{ minWidth: 100 }}>
                  <Stack sx={{ minWidth: 0, width: 100 }}>
                    <Typography variant="overline" sx={styleSets.lineCamp(1)}>
                      {node.user.formattedBirthday}
                    </Typography>
                  </Stack>
                </TableCellCustom>
                <TableCellCustom userId={node.user.id} sx={{ minWidth: 100 }}>
                  <Stack sx={{ minWidth: 0, width: 100 }}>
                    <Typography variant="overline" sx={styleSets.lineCamp(1)}>
                      {node.user.formattedPhoneNumber}
                    </Typography>
                  </Stack>
                </TableCellCustom>
                <TableCellCustom userId={node.user.id} sx={{ minWidth: 100 }}>
                  <Stack sx={{ minWidth: 0, width: 100 }}>
                    <Typography variant="overline" sx={styleSets.lineCamp(1)}>
                      {node.user.address?.fullAddress}
                    </Typography>
                  </Stack>
                </TableCellCustom>
                {shouldShowMemberNumber && (
                  <TableCellCustom userId={node.user.id}>
                    <Typography variant="overline">
                      {node.formattedMembershipNumber || ''}
                    </Typography>
                  </TableCellCustom>
                )}
                <TableCellCustom userId={node.user.id}>
                  <Stack spacing={1}>
                    <MemberWhetTag month={node.continueMonth ?? 0} />
                    <Typography variant="overline" color="text.secondary">
                      {node.formattedCreatedAt}
                    </Typography>
                  </Stack>
                </TableCellCustom>
                {enableFavoriteMembers && (
                  <TableCellCustom userId={node.user.id}>
                    <Typography variant="overline">
                      {node.favoriteMember?.name || ''}
                    </Typography>
                  </TableCellCustom>
                )}
                <TableCellCustom userId={node.user.id}>
                  <Typography variant="overline">
                    {t(
                      `${node.paymentMethod ?? PaymentMethod.CreditCard}_${
                        getPlanInterval(node.subscription?.planId ?? '') ??
                        MembershipInterval.Month
                      }`
                    )}
                  </Typography>
                </TableCellCustom>
                <TableCellCustom userId={node.user.id} sx={{ minWidth: 100 }}>
                  <Stack sx={{ minWidth: 0 }}>
                    <Typography variant="overline" sx={styleSets.lineCamp(1)}>
                      {node.purchaseCount}
                    </Typography>
                  </Stack>
                </TableCellCustom>
                <TableCellCustom userId={node.user.id} sx={{ minWidth: 100 }}>
                  <Stack sx={{ minWidth: 0 }}>
                    <Typography variant="overline" sx={styleSets.lineCamp(1)}>
                      {node.ticketUnusedCount}
                    </Typography>
                  </Stack>
                </TableCellCustom>
              </Fragment>
            ))}
            loading={loading}
            hasNextPage={hasNextPage}
            onFetchMore={onFetchMore}
            colSpan={12}
            component={TableRow}
            emptyMessage={t('noMember')}
            loadingComponent={
              <>
                {Array.from(Array(3).keys()).map(key => (
                  <TableRow key={key}>
                    <TableCell padding="none" sx={{ px: 0, py: 3 }}>
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Skeleton variant="circular" width={28} height={28} />
                        <SkeletonText variant="medium" width="80%" />
                      </Stack>
                    </TableCell>
                    <TableCell padding="none" sx={{ px: 0, py: 3 }}>
                      <SkeletonText variant="caption" width={60} />
                    </TableCell>
                    <TableCell padding="none" sx={{ px: 0, py: 3 }}>
                      <Stack>
                        <SkeletonText variant="caption" width={60} />
                      </Stack>
                    </TableCell>
                    <TableCell padding="none" sx={{ px: 0, py: 3 }}>
                      <Stack>
                        <SkeletonText variant="caption" width={60} />
                      </Stack>
                    </TableCell>
                    <TableCell padding="none" sx={{ px: 0, py: 3 }}>
                      <Stack>
                        <SkeletonText variant="caption" width={60} />
                      </Stack>
                    </TableCell>
                    <TableCell padding="none" sx={{ px: 0, py: 3 }}>
                      <Stack>
                        <SkeletonText variant="caption" width={60} />
                      </Stack>
                    </TableCell>
                    <TableCell padding="none" sx={{ px: 0, py: 3 }}>
                      <Stack>
                        <SkeletonText variant="caption" width={60} />
                      </Stack>
                    </TableCell>
                    <TableCell padding="none" sx={{ px: 0, py: 3 }}>
                      <Stack>
                        <SkeletonText variant="caption" width={60} />
                      </Stack>
                    </TableCell>
                    <TableCell padding="none" sx={{ px: 0, py: 3 }}>
                      <Stack>
                        <SkeletonText variant="caption" width={60} />
                      </Stack>
                    </TableCell>
                    <TableCell padding="none" sx={{ px: 0, py: 3 }}>
                      <Stack>
                        <SkeletonText variant="caption" width={60} />
                      </Stack>
                    </TableCell>
                    <TableCell padding="none" sx={{ px: 0, py: 3 }}>
                      <Stack>
                        <SkeletonText variant="caption" width={60} />
                      </Stack>
                    </TableCell>
                    <TableCell padding="none" sx={{ px: 0, py: 3 }}>
                      <Stack>
                        <SkeletonText variant="caption" width={60} />
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </>
            }
          />
        </Table>
      </TableContainer>
    </CellFrame>
  )
}

type TableCellCustomProps = {
  userId: string
  sx?: SxProps
}
const TableCellCustom: Cell<TableCellCustomProps> = ({
  userId,
  children,
  sx,
}) => {
  const { router, generatePath, routerPages } = useRouter()
  return (
    <TableCell
      sx={{ px: 1, py: 3, ...sx }}
      onClick={() =>
        router.push(
          generatePath(routerPages.ConsoleUserDetail, {
            userId,
          })
        )
      }
    >
      {children}
    </TableCell>
  )
}
